import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { DeviceDetectorService } from "ngx-device-detector";
import { ConfigService } from "../config/config.service";
import { PlatformService } from "../platform/platform.service";

@Injectable({
  providedIn: "root",
})

/* La clase GfkService es responsable de inicializar e interactuar con la biblioteca Gfk, lo que
incluye detectar el dispositivo y la vista web del usuario, y enviar impresiones con parámetros
personalizados. */
export class GfkService {
  private renderer2: Renderer2;
  private gfkScriptPromise: Promise<void> = Promise.resolve();
  private constantesGlobales;

  constructor(
    rendererFactory: RendererFactory2,
    private deviceService: DeviceDetectorService,
    @Inject(DOCUMENT) private document: Document,
    private configService: ConfigService,
    private platformService: PlatformService,
  ) {
    this.renderer2 = rendererFactory.createRenderer(null, null);
    if (this.platformService.isBrowser()) {
      this.initGfk();
    }
  }

  /**
   * La función inicializa el script Gfk según el tipo de dispositivo y carga el archivo de
   * script apropiado.
   */
  public initGfk(): void {
    this.constantesGlobales = this.configService.getConstantesGlobales();
    if (this.constantesGlobales.gfk != true) {
      return;
    }
    let src;
    const existingScript = this.document.getElementById("gfkscript");
    if (existingScript) {
      existingScript.remove();
    }
    src = "assets/scripts/gfk.js";

    if (this.deviceService.isMobile() && (this.isInIOSWebView() || this.isInAndroidWebView())) {
      src = "assets/scripts/gfkIos.js";
      if (this.isInAndroidWebView()) {
        src = "assets/scripts/gfkAndroid.js";
      }
    }
    if (src) this.gfkScriptPromise = this.loadGfkScript(src);
  }

  /**
   * La función carga un script de una fuente determinada y devuelve una promesa que se
   * resuelve cuando el script se carga correctamente o lo rechaza con un error si el script no se carga.
   * @param src - El parámetro `src` es la URL de origen del script que debe cargarse.
   * @returns La función `loadGfkScript` devuelve una Promesa que se resuelve como `void`.
   */
  loadGfkScript(src): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = this.renderer2.createElement("script");
      script.id = "gfkscript";
      script.onload = () => {
        resolve();
      };
      script.onerror = () => {
        reject(new Error("El script de gfkS2s no se pudo cargar"));
      };
      script.src = src;
      this.renderer2.appendChild(this.document.head, script);
    });
  }

  /**
   * La función comprueba si el código se está ejecutando en un WebView de Android.
   * @returns La función `isInAndroidWebView()` devuelve un valor booleano.
   */
  isInAndroidWebView(): boolean {
    if (this.platformService.isBrowser()) {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /; wv/.test(userAgent);
    }
  }

  /**
   * La función comprueba si el código se ejecuta en un WebView de iOS.
   * @returns La función `isInIOSWebView()` devuelve un valor booleano.
   */
  isInIOSWebView(): boolean {
    if (this.platformService.isBrowser()) {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isIOS = /iphone|ipod|ipad/.test(userAgent);
      const notSafari = !/safari/.test(userAgent);
      const notStandalone = !("standalone" in window.navigator && window.navigator["standalone"]);

      return isIOS && notSafari && notStandalone;
    }
  }

  /**
   * La función envía un evento de impresión con parámetros personalizados a un script de
   * terceros.
   * @param {any} customParams - El parámetro customParams es un objeto que contiene parámetros o datos
   * adicionales que desea transmitir junto con la impresión. Se puede utilizar para proporcionar
   * información específica sobre la impresión, como datos demográficos del usuario, contexto o
   * cualquier otro dato relevante.
   */
  public impression(customParams: any): void {
    this.gfkScriptPromise
      .then(() => {
        // console.log("Gfk Impression");
        const agent = window["gfkS2s"].getAgent();
        agent.impression("default", customParams);
      })
      .catch((error) => {});
  }
}
